<template>
  <section class="tripadvisor-details">
    <ERating
      v-if="ratingIsValid"
      :value="averageRating"
      class="tripadvisor-details__rating"
      variant="tripadvisor"
      :reviews="reviewCount <= 0 ? null : reviewCount"
      :reviews-url="sourceUrl"
      reviews-text
      reviews-link-color="bare"
      data-test-id="tripadvisor-details-rating"
      @reviews-click="trackClick('reviews_count')"
    />
    <div class="tripadvisor-details__buttons">
      <ELinkBubble
        v-if="sourceUrl"
        :href="sourceUrl"
        :target="linkTarget"
        color="result"
        :size="isDesktopUp ? 'm' : 's'"
        class="tripadvisor-details__button"
        data-test-id="tripadvisor-button"
        rel="noopener"
        @click="trackClick('tripadvisor_button')"
      >
        <EIcon
          icon="source-tripadvisor"
          :size="isDesktopUp ? 'm' : 's'"
          class="tripadvisor-details__button-icon"
        />
        Tripadvisor
      </ELinkBubble>
      <ELinkBubble
        v-if="url"
        :href="url"
        :target="linkTarget"
        color="result"
        :size="isDesktopUp ? 'm' : 's'"
        class="tripadvisor-details__button"
        data-test-id="tripadvisor-website"
        rel="noopener"
        @click="trackClick('website_button')"
      >
        <EIcon
          icon="atlas"
          :size="isDesktopUp ? 'm' : 's'"
          class="tripadvisor-details__button-icon"
        />
        {{ $t('web.entities.tripadvisor.website') }}
      </ELinkBubble>
    </div>
    <dl class="tripadvisor-details__info">
      <div
        v-if="openingHours"
        class="tripadvisor-details__info-item tripadvisor-details__info-item--showhide"
        data-test-id="tripadvisor-opening-hours"
        @click="toggleOpeningHours"
        @mouseenter="openingHoursHovered = true"
        @mouseleave="openingHoursHovered = false"
      >
        <dt class="tripadvisor-details__info-label">
          {{ $t('web.entities.tripadvisor.openinghours') }}:
        </dt>
        <dd class="tripadvisor-details__info-value">
          <span
            :title="$t('web.entities.tripadvisor.openinghours.more')"
            :aria-expanded="openingHoursShown"
            :aria-controls="$id('opening-hours')"
            class="tripadvisor-details__current-hours"
            role="button"
            data-test-id="tripadvisor-details-current-hours"
          >
            <span
              class="tripadvisor-details__current-opening-hours"
              :class="openingHoursShown ? 'tripadvisor-details__current-opening-hours--hidden' : ''"
              data-test-id="tripadvisor-current-opening-hours"
            >
              {{ formattedOpeningHours }}
            </span>
            <EButton
              :icon="openingHoursShown ? 'chevron-up' : 'chevron-down'"
              size="s"
              variant="bare"
              class="tripadvisor-details__opening-hours-button"
              :force-hover="openingHoursHovered"
            />
          </span>
        </dd>
      </div>
      <EOpeningHours
        v-if="openingHours"
        v-show="openingHoursShown"
        :id="$id('opening-hours')"
        ref="openingHours"
        class="tripadvisor-details__opening-hours--default"
        :opening-hours="openingHours"
        :today="today"
      />
      <div
        v-if="displayAddress"
        class="tripadvisor-details__info-item"
      >
        <dt class="tripadvisor-details__info-label">
          {{ $t('web.entities.tripadvisor.address') }}:
        </dt>
        <dd v-safe-html="displayAddress" class="tripadvisor-details__info-value" />
      </div>
      <div
        v-if="phone"
        class="tripadvisor-details__info-item"
      >
        <dt class="tripadvisor-details__info-label">
          {{ $t('web.entities.tripadvisor.phone') }}:
        </dt>
        <dd class="tripadvisor-details__info-value">
          <ELink
            :href="phoneUrl"
            color="result"
            data-test-id="phone-link"
            :aria-label="accessiblePhone"
            @click="trackClick('phone_link')"
          >
            {{ phone }}
          </ELink>
        </dd>
      </div>
    </dl>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import { trackClick, TYPE_ENTITY } from '~~/search/common/js/client/analytics/rich-content';
import { prefersReducedMotion } from '~~/common/js/client/screen';
import { isPhoneNumber } from '~~/common/js/universal/validators';
import { currentOpeningHours, CLOSED } from '~/results/tripadvisor';
import EButton from '~~/common/vue/components/button';
import ELink from '~~/common/vue/components/link';
import ELinkBubble from '~~/common/vue/components/link-bubble';
import ERating from '~~/common/vue/components/rating';
import EIcon from '~~/common/vue/components/icon';
import screen from '~~/common/vue/mixins/screen';

import EOpeningHours from './opening-hours';

export const TRACK_ID = 'tripadvisor';

export default {
  name: 'EDetails',
  components: {
    EButton,
    ELink,
    ELinkBubble,
    ERating,
    EOpeningHours,
    EIcon,
  },
  mixins: [screen],
  props: {
    averageRating: {
      type: Number,
      default: 0,
    },
    displayAddress: {
      type: String,
      default: '',
    },
    openingHours: {
      type: [Object, null],
      default: null,
    },
    phoneNumbers: {
      type: Array,
      default: () => [],
    },
    reviewCount: {
      type: Number,
      default: 0,
    },
    sourceUrl: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    openingHoursShown: false,
    openingHoursHovered: false,
  }),
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('goodall', ['getExperiment']),
    ...mapGetters('user', ['linkTarget']),
    phone() {
      return isPhoneNumber(this.phoneNumbers[0]) && this.phoneNumbers[0];
    },
    phoneUrl() {
      const number = this.phone.replace(/\s/g, '');
      return `tel:${number}`;
    },
    // a mildly hacky way to get screen readers to read the number
    // as a phone number, by inserting spaces after every character
    accessiblePhone() {
      return this.phone.split('').join(' ');
    },
    today() {
      const withSundayStart = new Date().getDay();
      return withSundayStart === 0 ? 6 : withSundayStart - 1;
    },
    formattedOpeningHours() {
      const formattedHours = currentOpeningHours(this.openingHours, this.locale);

      if (formattedHours === CLOSED) {
        return this.$t('web.entities.tripadvisor.closedtoday');
      }

      return formattedHours;
    },
    ratingIsValid() {
      return this.averageRating !== 0.0;
    },
  },
  methods: {
    async toggleOpeningHours() {
      this.openingHoursShown = !this.openingHoursShown;
      if (this.openingHoursShown) {
        await this.$nextTick();
        this.$refs.openingHours?.$el.scrollIntoView?.({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
        });
      }

      trackClick({
        type: TYPE_ENTITY,
        name: TRACK_ID,
        target: 'opening_hours',
        property: this.openingHoursShown ? 'open' : 'closed',
      })();
    },
    trackClick(target) {
      trackClick({
        type: TYPE_ENTITY,
        name: TRACK_ID,
        target,
      })();
    },
  },
};
</script>

<style lang="scss" scoped>
$opening-hours-min-width: 230px;

.tripadvisor-details {
  margin-top: -$space-s;
}

.tripadvisor__details-heading {
  @extend %tripadvisor-heading-style;
}

.tripadvisor-details__rating {
  margin-bottom: $space-m;
}

.tripadvisor-details__buttons {
  margin-bottom: $space-1l;
  display: flex;
}

.tripadvisor-details__button {
  max-width: 50%;
  flex-shrink: 1;
  white-space: nowrap;

  & + & {
    margin-left: $space-1s;
  }
}

.tripadvisor-details__button-icon {
  color: var(--color-decorative-icon);
  margin-right: $space-2s;
}

.tripadvisor-details__info {
  margin: $space-m 0;
}

.tripadvisor-details__info-item {
  & + & {
    margin-top: $space-m;
  }
}

.tripadvisor-details__info-label,
.tripadvisor-details__info-value {
  display: inline;
}

.tripadvisor-details__info-label {
  font-weight: $font-weight-1l;

  .tripadvisor-details__info-item--showhide & {
    padding-right: $space-1s;
  }
}

.tripadvisor-details__info-value {
  margin: 0;

  .tripadvisor-details__info-item--showhide & {
    display: inline-flex;
  }
}

.tripadvisor-details__current-hours {
  display: flex;
  justify-content: space-between;
}

.tripadvisor-details__info-item--showhide {
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  padding-right: $space-2l;
  padding-bottom: $space-2s;
  position: relative;
  min-height: $space-3l;

  .tripadvisor-details__info-label,
  .tripadvisor-details__current-hours {
    align-items: center;
    display: inline-flex;
  }

  + .tripadvisor-details__opening-hours--default + .tripadvisor-details__info-item {
    @include divider('top');

    padding-top: $space-m;
  }
}

.tripadvisor-details__opening-hours--default {
  animation: e-slide-top $timing-1s $easing forwards,
    e-fade-in $timing-s $easing forwards;
  cursor: initial;
  min-width: $opening-hours-min-width;
  flex-basis: 100%;

  @include desktop {
    flex-basis: auto;
  }
}

.tripadvisor-details__opening-hours--expanded {
  width: 100%;
}

.tripadvisor-details__opening-hours-button {
  transition: color $timing-2s $easing;
  color: var(--color-decorative-icon);
  position: absolute;
  top: $space-2s;
  right: 0;
}

.tripadvisor-details__current-opening-hours {
  transition: opacity $timing-2s $easing;
  white-space: nowrap;
}

.tripadvisor-details__current-opening-hours--hidden {
  opacity: 0;
}
</style>
